<template>
  <div id="cobranca">
    <Header />

    <div class="contorno">
      <h3 class="mb-2">Painel TechnoPlus</h3>
      <p class="text-muted mb-4">
        Ferramentas administrativas e de configuração
      </p>

      <!-- Tabs -->
        <b-tabs v-model="tabIndex" content-class="mt-4">
          <!-- TAB 1: RESUMO MENSAL -->
          <b-tab title="Resumo Mensal" active>
            <b-row class="mb-4">
              <b-col lg="4">
                <label>Empresa (Opcional)</label>
                <b-form-select
                  v-model="resumo.idEmpresa"
                  :options="listaEmpresas"
                  @change="onEmpresaChangeResumo"
                >
                  <template #first>
                    <b-form-select-option :value="null"
                      >Todas as empresas</b-form-select-option
                    >
                  </template>
                </b-form-select>
              </b-col>

              <b-col lg="2">
                <label>Ano</label>
                <b-form-select
                  v-model="resumo.ano"
                  :options="anosDisponiveis"
                />
              </b-col>

              <b-col lg="3" class="d-flex align-items-end">
                <b-button
                  variant="primary"
                  class="mr-2"
                  @click="buscarResumoMensal"
                  :disabled="loadingResumo || loadingExport"
                >
                  <b-spinner
                    small
                    v-if="loadingResumo"
                    class="mr-1"
                  ></b-spinner>
                  {{ loadingResumo ? "Buscando..." : "Buscar" }}
                </b-button>

                <b-button
                  v-if="resumoMensal.length > 0"
                  variant="success"
                  @click="exportarResumoExcel"
                  :disabled="loadingResumo || loadingExport"
                >
                  <b-spinner small v-if="loadingExport" class="mr-1"></b-spinner>
                  <b-icon v-else icon="download" class="mr-1"></b-icon>
                  {{ loadingExport ? "Gerando arquivo..." : "Exportar Excel" }}
                </b-button>
              </b-col>
            </b-row>

            <!-- Base de contagem: adesoes concluidas x documentos cobrados pela Clicksign -->
            <b-row v-if="resumoMensal.length > 0" class="mb-3">
              <b-col lg="12">
                <b-form-radio-group
                  v-model="baseResumo"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                >
                  <b-form-radio value="clicksign">Documentos Clicksign</b-form-radio>
                  <b-form-radio value="adesoes">Adesoes concluidas</b-form-radio>
                </b-form-radio-group>
                <small class="text-muted ml-3">
                  <span v-if="baseResumo === 'adesoes'">
                    Candidatos que atingiram os status configurados. Base do faturamento do cliente.
                  </span>
                  <span v-else>
                    Documentos finalizados na Clicksign (concluidos + expirados com ao menos uma
                    assinatura). Base do custo cobrado pela Clicksign.
                  </span>
                </small>
              </b-col>
            </b-row>

            <!-- Tabela Resumo Mensal -->
            <b-row>
              <b-col lg="12">
                <b-table
                  v-if="resumoMensal.length > 0"
                  striped
                  hover
                  responsive
                  :items="resumoMensalComBreakdown"
                  :fields="camposResumo"
                  :per-page="perPageResumo"
                  :current-page="currentPageResumo"
                  sticky-header="600px"
                  :tbody-tr-class="rowClass"
                >
                  <template #cell(nomeEmpresa)="data">
                    <div @click="toggleExpand(data.item)" :style="{ cursor: data.item._isMain && data.item._hasBreakdown ? 'pointer' : 'default' }">
                      <!-- Linha principal -->
                      <span v-if="data.item._isMain">
                        <b-icon
                          v-if="data.item._hasBreakdown"
                          :icon="isExpanded(data.item.idEmpresa) ? 'chevron-down' : 'chevron-right'"
                          class="mr-2"
                        ></b-icon>
                        <strong>{{ data.value }}</strong>
                        <!-- Chips de status -->
                        <b-badge
                          v-for="status in data.item.statusList"
                          :key="status.id"
                          variant="secondary"
                          class="ml-1"
                          style="font-size: 0.7rem;"
                        >
                          {{ status.id }}
                        </b-badge>
                      </span>
                      <!-- Linha de breakdown -->
                      <span v-else class="ml-4 text-muted">
                        <small v-if="data.item._label">└─ {{ data.item._label }}</small>
                        <small v-else>└─ Status {{ data.item._statusId }} ({{ data.item._statusDescricao }})</small>
                      </span>
                    </div>
                  </template>

                  <template #cell(total)="data">
                    <b-badge :variant="data.item._isMain ? 'primary' : 'secondary'" class="p-2">
                      {{ data.value }}
                    </b-badge>
                  </template>
                </b-table>

                <div
                  v-else-if="!loadingResumo && buscaRealizadaResumo"
                  class="text-center text-muted py-5"
                >
                  <b-icon icon="inbox" font-scale="3" class="mb-3"></b-icon>
                  <p>
                    Nenhuma empresa com status finalizados configurados ou
                    nenhuma finalização no ano selecionado.
                  </p>
                </div>

                <div
                  v-else-if="!buscaRealizadaResumo"
                  class="text-center text-muted py-5"
                >
                  <b-icon icon="calendar3" font-scale="3" class="mb-3"></b-icon>
                  <p>
                    Selecione o ano e clique em "Buscar" para visualizar o
                    resumo mensal.
                  </p>
                </div>

                <!-- Paginação -->
                <b-pagination
                  v-if="resumoMensal.length > perPageResumo"
                  v-model="currentPageResumo"
                  :total-rows="resumoMensal.length"
                  :per-page="perPageResumo"
                  align="center"
                  class="mt-3"
                />
              </b-col>
            </b-row>
          </b-tab>

          <!-- TAB 2: VISUALIZAÇÃO DETALHADA -->
          <b-tab title="Visualização Detalhada">
            <!-- Filtros -->
            <b-row class="mb-4">
              <b-col lg="3">
                <label>Empresa</label>
                <b-form-select
                  v-model="filtros.idEmpresa"
                  :options="listaEmpresas"
                  @change="onEmpresaChange"
                >
                  <template #first>
                    <b-form-select-option :value="null"
                      >Selecione uma empresa</b-form-select-option
                    >
                  </template>
                </b-form-select>
              </b-col>

              <b-col lg="3">
                <label>Data Início</label>
                <b-form-datepicker
                  v-model="filtros.dataInicio"
                  placeholder="Selecione a data"
                  :date-format-options="{
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  }"
                />
              </b-col>

              <b-col lg="2">
                <label>Data Fim</label>
                <b-form-datepicker
                  v-model="filtros.dataFim"
                  placeholder="Selecione a data"
                  :date-format-options="{
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  }"
                />
              </b-col>

              <b-col lg="4" class="d-flex align-items-end">
                <b-button
                  variant="primary"
                  class="mr-2"
                  @click="buscarFinalizacoes"
                  :disabled="
                    !filtros.idEmpresa ||
                    !filtros.dataInicio ||
                    !filtros.dataFim ||
                    loading
                  "
                >
                  <b-spinner small v-if="loading" class="mr-1"></b-spinner>
                  {{ loading ? "Buscando..." : "Buscar" }}
                </b-button>

                <b-button
                  v-if="finalizacoes.length > 0"
                  variant="success"
                  @click="exportarExcel"
                  :disabled="loading"
                >
                  <b-icon icon="download" class="mr-1"></b-icon>
                  Exportar Excel
                </b-button>
              </b-col>
            </b-row>

            <!-- Resumo -->
            <b-row v-if="finalizacoes.length > 0" class="mb-3">
              <b-col lg="12">
                <b-alert show variant="info">
                  <div class="mb-2">
                    <strong>Total de Finalizações:</strong>
                    {{ finalizacoes.length }} | <strong>Período:</strong>
                    {{ formatarData(filtros.dataInicio) }} a
                    {{ formatarData(filtros.dataFim) }}
                  </div>
                  <!-- Resumo por Status -->
                  <div v-if="resumoPorStatus.length > 0">
                    <strong>Por Status:</strong>
                    <b-badge
                      v-for="resumo in resumoPorStatus"
                      :key="resumo.statusId"
                      variant="light"
                      class="ml-2 p-2"
                    >
                      <strong>[{{ resumo.statusId }}]</strong> {{ resumo.statusDescricao }}: <strong>{{ resumo.quantidade }}</strong>
                    </b-badge>
                  </div>
                </b-alert>
              </b-col>
            </b-row>

            <!-- Tabela de Resultados -->
            <b-row>
              <b-col lg="12">
                <b-table
                  v-if="finalizacoes.length > 0"
                  striped
                  hover
                  responsive
                  :items="finalizacoes"
                  :fields="camposTabela"
                  :per-page="perPage"
                  :current-page="currentPage"
                >
                  <template #cell(dataFinalizacao)="data">
                    {{ new Date(data.value).toLocaleString("pt-BR") }}
                  </template>
                </b-table>

                <div
                  v-else-if="!loading && buscaRealizada"
                  class="text-center text-muted py-5"
                >
                  <b-icon icon="inbox" font-scale="3" class="mb-3"></b-icon>
                  <p>Nenhuma finalização encontrada para este período.</p>
                </div>

                <div
                  v-else-if="!buscaRealizada"
                  class="text-center text-muted py-5"
                >
                  <b-icon icon="search" font-scale="3" class="mb-3"></b-icon>
                  <p>
                    Selecione os filtros e clique em "Buscar" para visualizar os
                    dados.
                  </p>
                </div>

                <!-- Paginação -->
                <b-pagination
                  v-if="finalizacoes.length > perPage"
                  v-model="currentPage"
                  :total-rows="finalizacoes.length"
                  :per-page="perPage"
                  align="center"
                  class="mt-3"
                />
              </b-col>
            </b-row>
          </b-tab>

          <!-- TAB 3: CONFIGURAÇÕES DE STATUS -->
          <b-tab title="Configurações de Status">
            <b-row class="mb-3">
              <b-col lg="12">
                <b-alert show variant="info">
                  <b-icon icon="info-circle" class="mr-2"></b-icon>
                  Configure quais status devem ser considerados "finalizados"
                  para cada empresa. Apenas empresas com status configurados
                  aparecerão no resumo mensal.
                </b-alert>
              </b-col>
            </b-row>

            <!-- Tabela de Configurações -->
            <b-row>
              <b-col lg="12">
                <b-table
                  v-if="listaEmpresasConfig.length > 0"
                  striped
                  hover
                  responsive
                  :items="listaEmpresasConfig"
                  :fields="camposConfig"
                  :per-page="perPageConfig"
                  :current-page="currentPageConfig"
                >
                  <template #cell(statusConfigurados)="data">
                    <b-badge
                      v-if="data.value.length === 0"
                      variant="warning"
                      class="p-2"
                    >
                      Não configurado
                    </b-badge>
                    <span v-else>
                      <b-badge
                        v-for="statusId in data.value"
                        :key="statusId"
                        variant="success"
                        class="mr-1 p-2"
                      >
                        {{ statusId }}
                      </b-badge>
                    </span>
                  </template>

                  <template #cell(acoes)="data">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      @click="abrirModalConfigurarStatusEmpresa(data.item)"
                    >
                      <b-icon icon="gear" class="mr-1"></b-icon>
                      Configurar
                    </b-button>
                  </template>
                </b-table>

                <div v-else class="text-center text-muted py-5">
                  <b-icon icon="building" font-scale="3" class="mb-3"></b-icon>
                  <p>Nenhuma empresa cadastrada no sistema.</p>
                </div>

                <!-- Paginação -->
                <b-pagination
                  v-if="listaEmpresasConfig.length > perPageConfig"
                  v-model="currentPageConfig"
                  :total-rows="listaEmpresasConfig.length"
                  :per-page="perPageConfig"
                  align="center"
                  class="mt-3"
                />
              </b-col>
            </b-row>
          </b-tab>

          <!-- TAB 4: CACHE DE CONFIGURAÇÕES -->
          <b-tab title="Cache de Configurações">
            <b-row class="mb-3">
              <b-col lg="12">
                <b-alert show variant="info">
                  <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                  Ao invalidar o cache, na próxima navegação de qualquer usuário da empresa selecionada,
                  o sistema irá recarregar automaticamente as configurações (blocos, cores, etc.) sem
                  precisar deslogar. Use após alterações diretas no banco de dados.
                </b-alert>
              </b-col>
            </b-row>

            <b-row class="align-items-end">
              <b-col lg="4">
                <label>Empresa</label>
                <b-form-select v-model="cache.idEmpresa" :options="listaEmpresas">
                  <template #first>
                    <b-form-select-option :value="null">Todas as empresas</b-form-select-option>
                  </template>
                </b-form-select>
              </b-col>
              <b-col lg="3" class="mt-3 mt-lg-0">
                <b-button variant="warning" @click="invalidarCache" :disabled="loadingCache">
                  <b-spinner small v-if="loadingCache" class="mr-1"></b-spinner>
                  <b-icon v-else icon="arrow-clockwise" class="mr-1"></b-icon>
                  {{ loadingCache ? 'Invalidando...' : 'Invalidar Cache' }}
                </b-button>
              </b-col>
            </b-row>
          </b-tab>

          <!-- TAB 5: INTEGRAÇÃO ERP -->
          <b-tab title="Integração ERP">
            <b-row class="mb-3">
              <b-col lg="12">
                <b-alert show variant="warning">
                  <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                  Use para candidatos com status <strong>1000 (Erro na Integração com ERP)</strong> para reprocessar a integração manualmente.
                </b-alert>
              </b-col>
            </b-row>

            <!-- Formulário de busca -->
            <b-row class="mb-4">
              <b-col lg="3">
                <label class="d-block">Buscar por</label>
                <b-form-radio-group
                  v-model="erp.tipoBusca"
                  :options="[{ text: 'CPF', value: 'cpf' }, { text: 'ID Solicitação', value: 'id' }]"
                  button-variant="outline-primary"
                  buttons
                  size="sm"
                  @change="erp.candidato = null; erp.resultado = null; erp.busca = ''"
                />
              </b-col>
              <b-col lg="3">
                <label>{{ erp.tipoBusca === 'cpf' ? 'CPF do Candidato' : 'ID da Solicitação' }}</label>
                <b-form-input
                  v-model="erp.busca"
                  :placeholder="erp.tipoBusca === 'cpf' ? '000.000.000-00' : '12345'"
                  @keyup.enter="buscarCandidatoErp"
                />
              </b-col>
              <b-col lg="2" class="d-flex align-items-end">
                <b-button
                  variant="primary"
                  :disabled="!erp.busca || erp.loadingBusca"
                  @click="buscarCandidatoErp"
                >
                  <b-spinner small v-if="erp.loadingBusca" class="mr-1"></b-spinner>
                  {{ erp.loadingBusca ? 'Buscando...' : 'Buscar' }}
                </b-button>
              </b-col>
            </b-row>

            <!-- Card do candidato encontrado -->
            <b-row v-if="erp.candidato" class="mb-4">
              <b-col lg="8">
                <b-card border-variant="secondary">
                  <b-card-title>
                    <b-icon icon="person-circle" class="mr-2"></b-icon>
                    {{ erp.candidato.nome }}
                  </b-card-title>
                  <b-row>
                    <b-col lg="4">
                      <small class="text-muted">CPF</small><br>
                      <strong>{{ erp.candidato.cpf }}</strong>
                    </b-col>
                    <b-col lg="4">
                      <small class="text-muted">ID Solicitação</small><br>
                      <strong>{{ erp.candidato.id }}</strong>
                    </b-col>
                    <b-col lg="4">
                      <small class="text-muted">Empresa</small><br>
                      <strong>{{ erp.candidato.nomeEmpresa }}</strong>
                    </b-col>
                  </b-row>
                  <b-row class="mt-2">
                    <b-col>
                      <small class="text-muted">Status atual</small><br>
                      <b-badge :variant="erp.candidato.status === 1000 ? 'danger' : 'secondary'" class="p-2">
                        {{ erp.candidato.status }} — {{ erp.candidato.statusDescricao }}
                      </b-badge>
                    </b-col>
                  </b-row>
                  <hr />
                  <b-button
                    variant="warning"
                    :disabled="erp.loadingIntegracao"
                    @click="refazerIntegracaoErp"
                  >
                    <b-spinner small v-if="erp.loadingIntegracao" class="mr-1"></b-spinner>
                    <b-icon v-else icon="arrow-repeat" class="mr-1"></b-icon>
                    {{ erp.loadingIntegracao ? 'Integrando...' : 'Refazer Integração com ERP' }}
                  </b-button>
                </b-card>
              </b-col>
            </b-row>

            <!-- Resultado da integração -->
            <b-row v-if="erp.resultado">
              <b-col lg="8">
                <b-alert :variant="erp.resultado.sucesso ? 'success' : 'danger'" show>
                  <b-icon :icon="erp.resultado.sucesso ? 'check-circle' : 'x-circle'" class="mr-2"></b-icon>
                  {{ erp.resultado.mensagem }}
                </b-alert>
              </b-col>
            </b-row>
          </b-tab>

        </b-tabs>
    </div>

    <!-- Modal: Configurar Status Finalizados -->
    <b-modal
      id="modal-configurar-status"
      title="Configurar Status Finalizados"
      @ok="salvarStatusFinalizados"
      @hidden="resetModal"
      size="lg"
    >
      <p class="text-muted">
        <strong>Empresa:</strong> {{ empresaSelecionadaNome }}
      </p>
      <p class="text-muted">
        Marque os status que devem ser considerados como "finalizados" para cobrança:
      </p>

      <!-- Filtro de Visualização -->
      <b-row class="mb-3">
        <b-col lg="12">
          <b-form-group label="Modo de visualização:" label-class="font-weight-bold">
            <b-form-radio-group
              v-model="modoVisualizacaoStatus"
              :options="opcoesVisualizacaoStatus"
              button-variant="outline-primary"
              buttons
              size="sm"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Container com scroll -->
      <div class="modal-status-scroll">
        <!-- Visualização por Agrupamento -->
        <div v-if="modoVisualizacaoStatus === 'agrupamento'">
          <div v-for="grupo in statusPorAgrupamento" :key="grupo.nome" class="mb-4">
            <h6 class="text-primary font-weight-bold">
              <b-icon :icon="grupo.icone" class="mr-2"></b-icon>
              {{ grupo.nome }}
            </h6>
            <hr class="mt-1 mb-2" />
            <b-form-checkbox-group v-model="statusSelecionados" stacked>
              <b-form-checkbox
                v-for="status in grupo.status"
                :key="status.id"
                :value="status.id"
                class="mb-1"
              >
                {{ status.id }} - {{ status.descricao }}
              </b-form-checkbox>
            </b-form-checkbox-group>
          </div>
        </div>

        <!-- Visualização por Ordem -->
        <div v-else>
          <b-form-group>
            <b-form-checkbox-group v-model="statusSelecionados" stacked>
              <b-form-checkbox
                v-for="status in todosStatusOrdenados"
                :key="status.id"
                :value="status.id"
                class="mb-1"
              >
                {{ status.id }} - {{ status.descricao }}
              </b-form-checkbox>
            </b-form-checkbox-group>
          </b-form-group>
        </div>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancelar
        </b-button>
        <b-button variant="primary" @click="ok()">
          Salvar
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import api from "@/services/api";
import Header from "@/components/Header.vue";
import validaCredencial from "@/pages/web_cliente/utils";
import dayjs from "dayjs";

export default {
  name: "Cobranca",
  components: { Header },

  data() {
    const anoAtual = new Date().getFullYear();
    return {
      // Controle de tabs
      tabIndex: 0,

      // Tab 1: Resumo Mensal
      loadingResumo: false,
      loadingExport: false, // separada da busca: senao o botao Buscar vira "Buscando..."
      buscaRealizadaResumo: false,
      resumo: {
        idEmpresa: null,
        ano: anoAtual,
      },
      anosDisponiveis: [],
      resumoMensal: [],
      baseResumo: "clicksign", // 'clicksign' (principal) | 'adesoes'
      totalGeral: null,
      empresasExpandidas: [], // IDs das empresas com breakdown expandido
      camposResumo: [
        {
          key: "nomeEmpresa",
          label: "Empresa",
          sortable: true,
          stickyColumn: true,
        },
        { key: "jan", label: "Jan", sortable: true, class: "text-center" },
        { key: "fev", label: "Fev", sortable: true, class: "text-center" },
        { key: "mar", label: "Mar", sortable: true, class: "text-center" },
        { key: "abr", label: "Abr", sortable: true, class: "text-center" },
        { key: "mai", label: "Mai", sortable: true, class: "text-center" },
        { key: "jun", label: "Jun", sortable: true, class: "text-center" },
        { key: "jul", label: "Jul", sortable: true, class: "text-center" },
        { key: "ago", label: "Ago", sortable: true, class: "text-center" },
        { key: "set", label: "Set", sortable: true, class: "text-center" },
        { key: "out", label: "Out", sortable: true, class: "text-center" },
        { key: "nov", label: "Nov", sortable: true, class: "text-center" },
        { key: "dez", label: "Dez", sortable: true, class: "text-center" },
        { key: "total", label: "Total", sortable: true, class: "text-center" },
      ],
      perPageResumo: 500, // lista completa - a tabela ja rola com sticky-header
      currentPageResumo: 1,

      // Tab 2: Visualização Detalhada
      loading: false,
      buscaRealizada: false,
      filtros: {
        idEmpresa: null,
        dataInicio: null,
        dataFim: null,
      },
      finalizacoes: [],
      resumoPorStatus: [], // Resumo agrupado por status
      camposTabela: [
        { key: "idSolicitacao", label: "ID", sortable: true },
        { key: "cpf", label: "CPF", sortable: true },
        { key: "nome", label: "Nome", sortable: true },
        { key: "nomeFuncao", label: "Função", sortable: true },
        { key: "statusDescricao", label: "Status", sortable: true },
        { key: "dataFinalizacao", label: "Data Finalização", sortable: true },
      ],
      perPage: 20,
      currentPage: 1,

      // Tab 3: Configurações
      listaEmpresasConfig: [],
      camposConfig: [
        { key: "nomeEmpresa", label: "Empresa", sortable: true },
        {
          key: "statusConfigurados",
          label: "Status Finalizados",
          sortable: false,
        },
        { key: "acoes", label: "Ações", sortable: false, class: "text-center" },
      ],
      perPageConfig: 15,
      currentPageConfig: 1,

      // Listas globais
      listaEmpresas: [],
      todosStatus: [],

      // Modal
      empresaSelecionadaId: null,
      empresaSelecionadaNome: "",
      statusSelecionados: [],
      modoVisualizacaoStatus: "agrupamento", // 'agrupamento' ou 'ordem'
      opcoesVisualizacaoStatus: [
        { text: "Por agrupamento", value: "agrupamento" },
        { text: "Por ordem", value: "ordem" },
      ],

      // Tab 4: Cache de Configurações
      cache: { idEmpresa: null },
      loadingCache: false,

      // Tab 5: Integração ERP
      erp: {
        tipoBusca: 'cpf',
        busca: '',
        candidato: null,
        loadingBusca: false,
        loadingIntegracao: false,
        resultado: null,
      },

      // Estilo
      bgColor: "#a1a1a1",
    };
  },

  computed: {
    cssVars() {
      return {
        "--bg-color": this.bgColor,
      };
    },

    // Computed para tabela com breakdown expandido
    resumoMensalComBreakdown() {
      const resultado = [];

      const porClicksign = this.baseResumo === 'clicksign';

      this.resumoMensal.forEach(empresa => {
        // Na base Clicksign os meses vem de empresa.clicksign.
        // O breakdown e por status, entao nao se aplica a essa base.
        resultado.push({
          ...empresa,
          ...(porClicksign ? empresa.clicksign : {}),
          _isMain: true,
          _hasBreakdown: empresa.isMultiStatus && !porClicksign
        });

        // Base Clicksign: linha principal fica intacta (so o registro).
        // Abaixo, a quebra entre concluido e expirado.
        if (porClicksign) {
          resultado.push(...this.linhasDetalheClicksign(empresa));
        }

        // Adiciona linhas de breakdown se estiver expandido
        if (!porClicksign && empresa.isMultiStatus && this.isExpanded(empresa.idEmpresa) && empresa.breakdown) {
          empresa.breakdown.forEach(status => {
            resultado.push({
              ...status,
              nomeEmpresa: status.statusDescricao,
              _isMain: false,
              _statusId: status.statusId,
              _statusDescricao: status.statusDescricao
            });
          });
        }
      });

      // Linha mestra com a soma de todas as empresas
      if (this.totalGeral) {
        const base = porClicksign
          ? this.totalGeral.clicksign
          : this.totalGeral.adesoes;

        resultado.push({
          ...base,
          nomeEmpresa: 'TOTAL GERAL',
          _isMain: true,
          _isMaster: true,
          _hasBreakdown: false,
          statusList: []
        });

        if (porClicksign) {
          resultado.push(...this.linhasDetalheClicksign(this.totalGeral));
        }
      }

      return resultado;
    },

    todosStatusOrdenados() {
      return [...this.todosStatus].sort((a, b) => a.id - b.id);
    },

    statusPorAgrupamento() {
      // Mapeamento conforme CLAUDE.md
      const grupos = {
        "Em andamento": {
          ids: [1, 4, 5, 8, 23, 24, 25, 26, 27, 28],
          icone: "clock-history",
        },
        Rejeitados: { ids: [2, 3, 11, 17], icone: "x-circle" },
        "Em validação": { ids: [6, 7, 16, 18], icone: "shield-check" },
        "Em aprovação": { ids: [9, 21], icone: "hourglass-split" },
        Concluídos: { ids: [10, 22], icone: "check-circle" },
        Alertas: { ids: [12, 1000, 1010], icone: "exclamation-triangle" },
      };

      return Object.entries(grupos)
        .map(([nome, config]) => ({
          nome,
          icone: config.icone,
          status: this.todosStatus.filter((s) => config.ids.includes(s.id)),
        }))
        .filter((grupo) => grupo.status.length > 0); // Remove grupos vazios
    },
  },

  methods: {
    // ===== Tab 1: Resumo Mensal =====
    async buscarResumoMensal() {
      try {
        this.loadingResumo = true;
        this.buscaRealizadaResumo = true;

        const response = await api.post("web-cliente/cobranca/resumo-mensal", {
          ano: this.resumo.ano,
          idEmpresa: this.resumo.idEmpresa,
        });

        this.resumoMensal = response.data.resumo;
        this.totalGeral = response.data.totalGeral || null;
        this.currentPageResumo = 1;

        if (this.resumoMensal.length === 0) {
          this.msg(
            "Atenção",
            "Nenhuma empresa com finalizações encontradas para este ano",
            "warning",
          );
        }
      } catch (error) {
        this.msg("Erro", "Falha ao buscar resumo mensal", "danger");
      } finally {
        this.loadingResumo = false;
      }
    },

    onEmpresaChangeResumo() {
      this.resumoMensal = [];
      this.totalGeral = null;
      this.buscaRealizadaResumo = false;
    },

    toggleExpand(empresa) {
      if (!empresa._isMain || !empresa._hasBreakdown) return;

      const index = this.empresasExpandidas.indexOf(empresa.idEmpresa);
      if (index > -1) {
        this.empresasExpandidas.splice(index, 1);
      } else {
        this.empresasExpandidas.push(empresa.idEmpresa);
      }
    },

    isExpanded(idEmpresa) {
      return this.empresasExpandidas.includes(idEmpresa);
    },

    rowClass(item) {
      if (!item) return '';
      if (item._isMaster) return 'table-primary font-weight-bold';
      if (item._isMain) return '';
      return 'table-secondary'; // Fundo cinza para linhas de breakdown/detalhe
    },

    gerarAnosDisponiveis() {
      const anoAtual = new Date().getFullYear();
      const anos = [];
      for (let i = anoAtual; i >= anoAtual - 5; i--) {
        anos.push({ value: i, text: i.toString() });
      }
      this.anosDisponiveis = anos;
    },

    // Sub-linhas da base Clicksign: a quebra do total em concluidos e expirados.
    linhasDetalheClicksign(fonte) {
      const linhas = [
        { ...fonte.concluidos, _label: 'Concluidos (todos assinaram)' },
        { ...fonte.expirados, _label: 'Expirados (prazo venceu com assinatura)' }
      ];

      return linhas.map(linha => ({ ...linha, nomeEmpresa: '', _isMain: false }));
    },

    async exportarResumoExcel() {
      try {
        this.loadingExport = true;

        const response = await api.post(
          "web-cliente/cobranca/resumo-mensal?exportar=true",
          {
            ano: this.resumo.ano,
            idEmpresa: this.resumo.idEmpresa,
            base: this.baseResumo,
            // manda o resumo que a tela ja carregou: o servidor so formata,
            // em vez de refazer o calculo inteiro
            resumo: this.resumoMensal,
          },
          { responseType: "blob" },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const nomeArquivo = this.resumo.idEmpresa
          ? `resumo_mensal_${
              this.listaEmpresas.find((e) => e.value === this.resumo.idEmpresa)
                ?.text
            }_${this.resumo.ano}.xlsx`
          : `resumo_mensal_${this.resumo.ano}.xlsx`;

        link.setAttribute("download", nomeArquivo);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.msg("Sucesso", "Excel exportado com sucesso", "success");
      } catch (error) {
        this.msg("Erro", "Falha ao exportar Excel", "danger");
      } finally {
        this.loadingExport = false;
      }
    },

    // ===== Tab 2: Visualização Detalhada =====
    async buscarFinalizacoes() {
      try {
        this.loading = true;
        this.buscaRealizada = true;

        const response = await api.post("web-cliente/cobranca/buscar", {
          idEmpresa: this.filtros.idEmpresa,
          dataInicio: this.filtros.dataInicio,
          dataFim: this.filtros.dataFim,
        });

        this.finalizacoes = response.data.finalizacoes;
        this.resumoPorStatus = response.data.resumoPorStatus || [];
        this.currentPage = 1;

        if (this.finalizacoes.length === 0) {
          this.msg(
            "Atenção",
            "Nenhuma finalização encontrada para este período",
            "warning",
          );
        }
      } catch (error) {
        if (error.response?.data?.hint) {
          this.msg("Atenção", error.response.data.hint, "warning");
        } else {
          this.msg("Erro", "Falha ao buscar finalizações", "danger");
        }
      } finally {
        this.loading = false;
      }
    },

    async exportarExcel() {
      try {
        this.loading = true;

        const response = await api.post(
          "web-cliente/cobranca/buscar?exportar=true",
          {
            idEmpresa: this.filtros.idEmpresa,
            dataInicio: this.filtros.dataInicio,
            dataFim: this.filtros.dataFim,
          },
          { responseType: "blob" },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const empresaNome =
          this.listaEmpresas.find((e) => e.value === this.filtros.idEmpresa)
            ?.text || "empresa";
        link.setAttribute(
          "download",
          `cobranca_${empresaNome}_${this.filtros.dataInicio}_${this.filtros.dataFim}.xlsx`,
        );

        document.body.appendChild(link);
        link.click();
        link.remove();

        this.msg("Sucesso", "Excel exportado com sucesso", "success");
      } catch (error) {
        this.msg("Erro", "Falha ao exportar Excel", "danger");
      } finally {
        this.loading = false;
      }
    },

    onEmpresaChange() {
      this.finalizacoes = [];
      this.buscaRealizada = false;
    },

    // ===== Tab 3: Configurações =====
    async carregarEmpresasConfig() {
      try {
        const response = await api.get("web-cliente/cobranca/empresas");
        this.listaEmpresasConfig = response.data.map((emp) => ({
          id: emp.id,
          nomeEmpresa: emp.nomeEmpresa,
          statusConfigurados: emp.status_finalizados || [],
        }));
      } catch (error) {
        this.msg(
          "Erro",
          "Falha ao carregar configurações de empresas",
          "danger",
        );
      }
    },

    abrirModalConfigurarStatusEmpresa(empresa) {
      this.empresaSelecionadaId = empresa.id;
      this.empresaSelecionadaNome = empresa.nomeEmpresa;
      this.statusSelecionados = empresa.statusConfigurados || [];
      this.$bvModal.show("modal-configurar-status");
    },

    async salvarStatusFinalizados() {
      try {
        await api.put("web-cliente/cobranca/status-finalizados", {
          idEmpresa: this.empresaSelecionadaId,
          statusFinalizados: this.statusSelecionados,
        });

        this.msg(
          "Sucesso",
          "Status finalizados atualizados com sucesso",
          "success",
        );

        // Atualizar lista local (Tab 3)
        const empresa = this.listaEmpresasConfig.find(
          (e) => e.id === this.empresaSelecionadaId,
        );
        if (empresa) {
          empresa.statusConfigurados = this.statusSelecionados;
        }

        // Atualizar lista dropdown (Tab 2)
        const empresaDropdown = this.listaEmpresas.find(
          (e) => e.value === this.empresaSelecionadaId,
        );
        if (empresaDropdown) {
          empresaDropdown.statusFinalizados = this.statusSelecionados;
        }
      } catch (error) {
        this.msg("Erro", "Falha ao salvar status finalizados", "danger");
      }
    },

    resetModal() {
      this.empresaSelecionadaId = null;
      this.empresaSelecionadaNome = "";
      this.statusSelecionados = [];
    },

    // ===== Funções compartilhadas =====
    async carregarEmpresas() {
      try {
        this.loading = true;
        const response = await api.get("web-cliente/cobranca/empresas");
        this.listaEmpresas = response.data.map((emp) => ({
          value: emp.id,
          text: emp.nomeEmpresa,
          statusFinalizados: emp.status_finalizados,
        }));
      } catch (error) {
        this.msg("Erro", "Falha ao carregar empresas", "danger");
      } finally {
        this.loading = false;
      }
    },

    async carregarStatus() {
      try {
        const response = await api.get("web-cliente/cobranca/status");
        this.todosStatus = response.data;
      } catch (error) {
        this.msg("Erro", "Falha ao carregar status", "danger");
      }
    },

    formatarData(data) {
      if (!data) return "";
      // Usar dayjs para evitar problema de UTC (não subtrai 1 dia)
      return dayjs(data).format("DD/MM/YYYY");
    },

    async invalidarCache() {
      this.loadingCache = true
      try {
        const response = await api.post('web-cliente/cobranca/invalidar-config', {
          idEmpresa: this.cache.idEmpresa
        })
        this.msg('Cache Invalidado', response.data.message, 'success', 4000)
      } catch (error) {
        this.msg('Erro', 'Falha ao invalidar cache', 'danger')
      } finally {
        this.loadingCache = false
      }
    },

    // ===== Tab 5: Integração ERP =====
    async buscarCandidatoErp() {
      this.erp.candidato = null;
      this.erp.resultado = null;
      this.erp.loadingBusca = true;
      try {
        const body = this.erp.tipoBusca === 'cpf'
          ? { cpf: this.erp.busca }
          : { idSolicitacao: this.erp.busca };
        const response = await api.post('web-cliente/cobranca/buscar-candidato-erp', body);
        this.erp.candidato = response.data;
      } catch (error) {
        const mensagem = error.response?.data?.error || 'Candidato não encontrado';
        this.msg('Atenção', mensagem, 'warning');
      } finally {
        this.erp.loadingBusca = false;
      }
    },

    async refazerIntegracaoErp() {
      this.erp.resultado = null;
      this.erp.loadingIntegracao = true;
      try {
        const response = await api.post(`webhooks/clicksignServices/${this.erp.candidato.id}/api`);
        const matricula = response.data?.matricula;
        this.erp.resultado = {
          sucesso: true,
          mensagem: `Integração realizada com sucesso para ${this.erp.candidato.nome}${matricula ? ` — Matrícula: ${matricula}` : ''}.`,
        };
      } catch (error) {
        const mensagem = error.response?.data?.error || error.response?.data?.message || 'Falha na integração com ERP';
        this.erp.resultado = { sucesso: false, mensagem };
      } finally {
        this.erp.loadingIntegracao = false;
      }
    },

    msg(titulo, texto, cor, time = 5000) {
      this.$bvToast.toast(texto, {
        title: titulo,
        variant: cor,
        solid: true,
        autoHideDelay: time,
      });
    },
  },

  async created() {
    // Validar permissão 999
    const acesso = await validaCredencial(999);
    if (acesso === "fail") {
      this.msg("ATENÇÃO", "Sem permissão para acessar esta página", "warning");
      return this.$router.push("/candidatos");
    }

    // Carregar dados iniciais
    await this.carregarEmpresas();
    await this.carregarStatus();
    await this.carregarEmpresasConfig();
    this.gerarAnosDisponiveis();

    // Buscar cor da empresa (se tiver)
    const idEmpresa = localStorage.getItem("idEmpresa");
    if (idEmpresa) {
      try {
        const response = await api.get(`empresas/${idEmpresa}`);
        this.bgColor = response.data.cor || "#a1a1a1";
      } catch (error) {
        // Ignora erro de cor
      }
    }

    // Carregar resumo mensal automaticamente na Tab 1
    await this.buscarResumoMensal();
  },
};
</script>

<style scoped>
.contorno {
  padding: 2rem;
}

.jumbotron {
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

label {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

/* Estilo para células com números */
.text-center {
  text-align: center;
}

/* Sticky column para nome da empresa */
::v-deep .b-table-sticky-column {
  position: sticky;
  left: 0;
  background-color: white;
  z-index: 1;
}

/* Botões de radio em formato toggle */
::v-deep .btn-group-toggle .btn {
  min-width: 150px;
}

/* Container com scroll para lista de status */
.modal-status-scroll {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

/* Scrollbar customizada */
.modal-status-scroll::-webkit-scrollbar {
  width: 8px;
}

.modal-status-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-status-scroll::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

.modal-status-scroll::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* Estilo para linhas de breakdown */
::v-deep .table-secondary {
  background-color: #f8f9fa !important;
  font-size: 0.9em;
}

/* Cursor pointer para linhas expansíveis */
::v-deep tr:has([data-expandable="true"]) {
  cursor: pointer;
}
</style>
